import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Tag, Button, Progress, Tooltip, Empty, Popover, Space, Input
} from 'antd';
import { 
  ArrowLeftOutlined,
  BulbOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { analyzeDriverWithRuleset } from '../utils/OTRKPIAIRuleset';
import OTRInsightCard from './OTRInsightCard';
import useAllEmployees from '../../../hooks/useAllEmployees';
import { values } from 'lodash';
import { employeeModelData } from '../../../services/ModelService';

const OTRDriverPerformanceTab = ({ 
  driverData, 
  filteredStopData, 
  tripData,
  tripStartTimes 
}) => {
  const [selectedDriverDetail, setSelectedDriverDetail] = useState(null);
  const [driverTripData, setDriverTripData] = useState([]);
  const [driverSearchText, setDriverSearchText] = useState('');
  const { allEmployees } = useAllEmployees();
  const [allEmployeeRecords, setAllEmployeeRecords] = useState(null);

  // Load all employee records including details
  useEffect(() => {
    const loadAllEmployeeRecords = async () => {
      try {
        const allRecords = await employeeModelData.getAll();
        setAllEmployeeRecords(allRecords);
      } catch (error) {
        console.error('Error loading employee records:', error);
      }
    };

    loadAllEmployeeRecords();
  }, []);

  // Function to get driver name by driver ID (which maps to Driver Authority Number)
  const getDriverName = (driverId) => {
    if (!allEmployeeRecords || !driverId) return 'Unknown Driver';
    
    // Get all employee records and separate them by type
    const xeroEmployeeArray = values(allEmployeeRecords).filter(e => e.sk && e.sk.endsWith('#EXTERNAL') && !e.isDeleted);
    const detailsEmployeeArray = values(allEmployeeRecords).filter(e => e.sk && e.sk.endsWith('#DETAILS') && !e.isDeleted);
    
    // Find the employee details record that has matching driver authority number
    const employeeDetailsRecord = detailsEmployeeArray.find(record => 
      String(record.daNumber) === String(driverId)
    );
    
    if (employeeDetailsRecord) {
      // Extract employeeID from the details record key
      const employeeID = employeeDetailsRecord.sk.match(/#EMPLOYEE#(.+)#DETAILS/)?.[1];
      
      if (employeeID) {
        // Find the corresponding external record
        const employeeExternal = xeroEmployeeArray.find(emp => 
          emp.employeeID === employeeID
        );
        
        if (employeeExternal && employeeExternal.firstName && employeeExternal.lastName) {
          return `${employeeExternal.firstName} ${employeeExternal.lastName}`;
        }
      }
    }
    
    return `Driver ${driverId}`;
  };

  if (driverData.length === 0) {
    return (
      <Empty 
        description="No driver data available" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  }

  const handleDriverClick = async (driverId) => {
    setSelectedDriverDetail(driverId);
    // Filter trip data for this driver from already filtered tripData
    // tripData is processed from filtered data in loadData(), so this maintains filter consistency
    const driverTrips = tripData.filter(trip => trip.driverId === driverId);
    setDriverTripData(driverTrips);
  };

  const renderDriverTripView = () => {
    if (!selectedDriverDetail || driverTripData.length === 0) {
      return null;
    }

    const columns = [
      {
        title: 'Trip Start',
        dataIndex: 'tripId',
        key: 'tripId',
        sorter: (a, b) => String(a.tripId || '').localeCompare(String(b.tripId || '')),
        render: (_, record) => tripStartTimes[record.tripKey]?.time || record.tripId,
      },
      {
        title: 'Route',
        dataIndex: 'route',
        key: 'route',
        sorter: (a, b) => String(a.route || '').localeCompare(String(b.route || '')),
      },
      {
        title: 'On-Time %',
        dataIndex: ['overallStats', 'onTimePercent'],
        key: 'onTimePercent',
        render: val => {
          const value = val || 0;
          let color = 'green';
          if (value < 70) color = 'blue';
          else if (value < 90) color = 'red';
          
          return (
            <Tooltip title={`${Math.round(value)}% on time`}>
              <Progress 
                percent={Math.round(value)} 
                size="small" 
                strokeColor={color}
                format={percent => `${percent}%`}
              />
            </Tooltip>
          );
        },
        sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      },
      {
        title: 'First Stop OTR',
        dataIndex: ['firstStopStats', 'onTimePercent'],
        key: 'firstStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.firstStopStats?.onTimePercent || 0) - (b.firstStopStats?.onTimePercent || 0),
      },
      {
        title: 'Middle Stop OTR',
        dataIndex: ['midStopStats', 'onTimePercent'],
        key: 'midStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.midStopStats?.onTimePercent || 0) - (b.midStopStats?.onTimePercent || 0),
      },
      {
        title: 'Last Stop OTR',
        dataIndex: ['lastStopStats', 'onTimePercent'],
        key: 'lastStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.lastStopStats?.onTimePercent || 0) - (b.lastStopStats?.onTimePercent || 0),
      },
      {
        title: 'Total Stops',
        dataIndex: 'stopCount',
        key: 'stopCount',
        sorter: (a, b) => (a.stopCount || 0) - (b.stopCount || 0),
      }
    ];

    return (
      <Card 
        title={
          <Space>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => setSelectedDriverDetail(null)}
            >
              Back
            </Button>
            {`Trip Performance for ${getDriverName(selectedDriverDetail)}`}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={driverTripData}
          rowKey="tripId"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'driverId',
      key: 'driverId',
      sorter: (a, b) => String(a.driverId || '').localeCompare(String(b.driverId || '')),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleDriverClick(record.driverId)}
        >
          {text}
        </Button>
      ),
      width: 100,
    },
    {
      title: 'Driver',
      dataIndex: 'driverId',
      key: 'driverName',
      sorter: (a, b) => getDriverName(a.driverId).localeCompare(getDriverName(b.driverId)),
      render: (driverId, record) => (
        <span>{getDriverName(driverId)}</span>
      ),
      width: 200,
    },
    {
      title: 'On-Time %',
      dataIndex: ['overallStats', 'onTimePercentage'],
      key: 'onTimePercent',
      render: val => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue';
        else if (value < 90) color = 'red';
        
        return (
          <Tooltip title={`${Math.round(value)}% on time`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.overallStats?.onTimePercentage || 0) - (b.overallStats?.onTimePercentage || 0),
    },
    {
      title: 'Early %',
      dataIndex: ['overallStats', 'earlyPercentage'],
      key: 'earlyPercent',
      render: val => `${Math.round(val || 0)}%`,
      sorter: (a, b) => (a.overallStats?.earlyPercentage || 0) - (b.overallStats?.earlyPercentage || 0),
    },
    {
      title: 'Late %',
      dataIndex: ['overallStats', 'latePercentage'],
      key: 'latePercent',
      render: val => `${Math.round(val || 0)}%`,
      sorter: (a, b) => (a.overallStats?.latePercentage || 0) - (b.overallStats?.latePercentage || 0),
    },
    {
      title: 'AI Insights',
      key: 'insights',
      dataIndex: 'insights',
      render: (_, record) => {
        // Generate AI insights using the filtered data
        const aiInsight = analyzeDriverWithRuleset(record, filteredStopData);
        
        if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
          return <Tag color="default">No insights</Tag>;
        }
        
        // Create a condensed insight indicator based on the type of insight
        let insightType = "Issue";
        let tagColor = "#f50";
        
        // Categorize insights by common types for short display
        if (aiInsight.insight.includes("early departure")) {
          insightType = "Early departures";
          tagColor = "#fa8c16"; // Orange
        } else if (aiInsight.insight.includes("systematically late")) {
          insightType = "Consistently late";
          tagColor = "#1890ff"; // Blue
        } else if (aiInsight.insight.includes("inconsistency")) {
          insightType = "Inconsistent";
          tagColor = "#722ed1"; // Purple
        }
        
        // Add improvement percentage if available
        const improvementText = aiInsight.improvement ? ` +${aiInsight.improvement}%` : '';
        
        // Store insight type and improvement in record for sorting/filtering
        record.insightType = insightType;
        record.insightImprovement = aiInsight.improvement || 0;
        
        return (
          <Popover 
            content={
              <OTRInsightCard 
                title={`Driver ${record.driverId} Insights`}
                insight={aiInsight.insight}
                recommendation={aiInsight.recommendation}
                improvement={aiInsight.improvement}
                category="driver"
              />
            } 
            title={null}
            trigger="click"
          >
            <Tag 
              color={tagColor}
              icon={<BulbOutlined />}
              style={{ cursor: 'pointer' }}
            >
              {insightType}{improvementText}
            </Tag>
          </Popover>
        );
      },
      sorter: (a, b) => {
        const insightA = analyzeDriverWithRuleset(a, filteredStopData);
        const insightB = analyzeDriverWithRuleset(b, filteredStopData);
        
        const improvementA = insightA?.improvement || 0;
        const improvementB = insightB?.improvement || 0;
        
        return improvementB - improvementA; // Sort by highest improvement first
      },
      filters: [
        { text: 'Early departures', value: 'Early departures' },
        { text: 'Consistently late', value: 'Consistently late' },
        { text: 'Inconsistent', value: 'Inconsistent' },
        { text: 'No issues', value: 'No insights' }
      ],
      onFilter: (value, record) => {
        const aiInsight = analyzeDriverWithRuleset(record, filteredStopData);
        
        if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
          return value === 'No insights';
        }
        
        let insightType = "Issue";
        
        // Categorize for filtering
        if (aiInsight.insight.includes("early departure")) {
          insightType = "Early departures";
        } else if (aiInsight.insight.includes("systematically late")) {
          insightType = "Consistently late";
        } else if (aiInsight.insight.includes("inconsistency")) {
          insightType = "Inconsistent";
        }
        
        return insightType === value;
      },
      width: 180
    }
  ];

  const driverDataWithInsights = driverData.map(record => {
    const aiInsight = analyzeDriverWithRuleset(record, filteredStopData);
    let insightType = "Issue";
    if (aiInsight?.insight?.includes("early departure")) insightType = "Early departures";
    else if (aiInsight?.insight?.includes("systematically late")) insightType = "Consistently late";
    else if (aiInsight?.insight?.includes("inconsistency")) insightType = "Inconsistent";
    else if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) insightType = "No insights";
    return {
      ...record,
      aiInsight,
      insightType,
      insightImprovement: aiInsight?.improvement || 0,
    };
  });

  // Filter drivers based on search text
  const filteredDriverData = driverSearchText 
    ? driverDataWithInsights.filter(driver => 
        String(driver.driverId).toLowerCase().includes(driverSearchText.toLowerCase()) || 
        getDriverName(driver.driverId).toLowerCase().includes(driverSearchText.toLowerCase())
      )
    : driverDataWithInsights;

  return (
    <div>
      {selectedDriverDetail ? (
        renderDriverTripView()
      ) : (
        <Card title="Driver Performance">
          <div style={{ marginBottom: '16px' }}>
            <Input
              placeholder="Search by Driver ID or Driver Name"
              prefix={<SearchOutlined />}
              value={driverSearchText}
              onChange={(e) => setDriverSearchText(e.target.value)}
              style={{ width: '300px' }}
              allowClear
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredDriverData}
            rowKey="driverId"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      )}
    </div>
  );
};

export default OTRDriverPerformanceTab; 